import {useEffect} from 'react'
import PropTypes from 'prop-types'

// Singleton promise to prevent multiple script loads
let feefoScriptLoadingPromise = null

// Function to check if the Feefo loader script is already in the DOM
const isFeefoScriptAlreadyLoaded = () => {
    return Boolean(document.querySelector(`script[src*="api.feefo.com/api/javascript"]`))
}

// Function to dynamically load the Feefo loader script
const loadFeefoScript = (merchant) => {
    if (feefoScriptLoadingPromise) return feefoScriptLoadingPromise

    feefoScriptLoadingPromise = new Promise((resolve, reject) => {
        if (isFeefoScriptAlreadyLoaded()) {
            resolve()
            return
        }

        const FEEFO_LOADER_SRC = `https://api.feefo.com/api/javascript/${merchant}`
        const script = document.createElement('script')
        script.src = FEEFO_LOADER_SRC
        script.type = 'text/javascript'
        script.async = true

        script.onload = resolve
        script.onerror = (event) => {
            console.error('Failed to load Feefo script:', event)
            script.remove()
            feefoScriptLoadingPromise = null
            reject(new Error('Failed to load Feefo script.'))
        }

        document.head.appendChild(script)
    })

    return feefoScriptLoadingPromise
}

// Call this in components that render Feefo widget elements (e.g. ReviewScoreIcon)
// to trigger a re-scan of the DOM after SPA navigation.
export const reinitFeefo = () => {
    // Preferred: use the widget instance API if available
    if (window.feefoWidgetInstance?.init) {
        window.feefoWidgetInstance.init()
        return
    }

    // Fallback: remove and re-inject feefo-widget.js to force a fresh DOM scan.
    // The browser serves it from cache so there is no network overhead.
    const widgetScript = document.querySelector('script[src*="feefo-widget-v2/js/feefo-widget.js"]')
    if (!widgetScript) return

    const src = widgetScript.src
    widgetScript.remove()
    delete window.feefoWidgetInstance

    const script = document.createElement('script')
    script.src = src
    script.type = 'text/javascript'
    script.async = true
    script.onload = () => {
        if (window.feefoWidget) {
            window.feefoWidgetInstance = window.feefoWidget(window.feefoWidgetInstanceConfig)
        }
    }
    document.head.appendChild(script)
}

// Custom hook to load the Feefo loader script
export const useFeefo = (provider, merchant) => {
    useEffect(() => {
        if (provider !== 'feefo') return

        loadFeefoScript(merchant).catch(console.error)
    }, [provider, merchant])

    return {loadFeefoScript, reinitFeefo}
}

useFeefo.propTypes = {
    provider: PropTypes.string.isRequired,
    merchant: PropTypes.string,
}
